import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifyBoardSync, refreshBoard } from "./useBoardEvents";
import { useApi } from "./useApi";
import { useReplay } from "./ReplayContext";
import { useVoiceSession } from "./VoiceSessionContext";

const STEP_PAUSE_MS = 2000;

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function useReplayPlayback() {
  const api = useApi("sim");
  const queryClient = useQueryClient();
  const { newShift } = useVoiceSession();
  const { selected, selectScenario, setStepStates, setPlaying, setReplayOutcome } = useReplay();

  const play = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("Select a scenario");

      setPlaying(true);
      setReplayOutcome(null);

      const loaded = await api.simLoadScenario(selected.path);
      const scenarioPath = loaded.path ?? selected.path;
      const steps = loaded.steps ?? selected.steps ?? [];
      selectScenario({
        ...selected,
        name: loaded.name,
        path: scenarioPath,
        step_count: steps.length,
        steps,
      });
      setStepStates(steps.map(() => "pending" as const));

      // Wipe sim operational state before scenario steps (clears DB + conflict/advisory RAM).
      // Isolation must not depend on YAML step 0 alone after a prior From live.
      await api.resetBoard();
      newShift();
      notifyBoardSync("sim");
      refreshBoard(queryClient, "sim");

      let lastResult: Awaited<ReturnType<typeof api.simPlayStep>> | null = null;
      for (let i = 0; i < steps.length; i++) {
        setStepStates((prev) => {
          const next = [...prev];
          next[i] = "active";
          return next;
        });

        const result = await api.simPlayStep(i, scenarioPath);
        lastResult = result;
        setStepStates((prev) => {
          const next = [...prev];
          next[i] = result.ok ? "ok" : "fail";
          return next;
        });

        notifyBoardSync("sim");
        refreshBoard(queryClient, "sim");

        if (!result.ok) break;

        if (i < steps.length - 1) {
          await sleep(STEP_PAUSE_MS);
        }
      }
      if (lastResult) {
        setReplayOutcome(lastResult);
      }
      return lastResult;
    },
    onSettled: () => {
      setPlaying(false);
      void queryClient.refetchQueries({ queryKey: ["sim-status"] });
      void queryClient.refetchQueries({ queryKey: ["sim-scenarios"] });
    },
  });

  return { play };
}
